'use client';

/** Hide social auth buttons until app store review is approved. Flip via Vercel env var. */
const ENABLE_SOCIAL_AUTH = process.env.NEXT_PUBLIC_ENABLE_SOCIAL_AUTH === 'true';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { type ChangeEvent, useCallback, useEffect, useMemo, useRef, useState, useTransition } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from '@/components/toast';
import { Spinner } from '@/components/ui/spinner';
import { loginWithPhone, login } from '../actions';
import BackgroundVideo from '@/components/BackgroundVideo';
import { signIn as nextAuthSignIn, useSession } from 'next-auth/react';
import { checkEmailDomain, type EmailDomainSuggestion } from '@/utils/emailDomainValidator';
import useIsWebview from '@/hooks/use-is-webview';
import { PierreRiveIdle } from '@/components/pierre-rive-idle';
import { AnimatePresence, motion } from 'framer-motion';
import { Eye, EyeOff, Mail, MessageCircle } from 'lucide-react';
import { EmailDomainAutocomplete } from '@/components/email-domain-autocomplete';
type LoginMethod = 'phone' | 'email';
import useRudderStackAnalytics from '@/hooks/useRudderAnalytics';
import { useFirstTouchUTM } from '@/hooks/use-first-touch-utm';
import { useIfpCampaignParams } from '@/hooks/use-ifp-campaign-params';
import { mergeTrackingParams } from '@/lib/utm-tracker';
import { getHomePage } from '@/hooks/use-home-page';
import { getSafeRedirectUrl } from '@/lib/utils';
import { sanitizeBrazilianPhone } from '@/utils/formatPhoneNumber';
import { loginErrorCodeFromStatus } from '@/lib/analytics/login-error-codes';
import { resolvePrefillEmail, setPrefillEmail } from '@/lib/auth/prefill-email';
import { usePageTransition } from '@/hooks/use-page-transition';
export default function LoginClient() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const callbackUrl = searchParams.get('callbackUrl');
  const [loginMethod, setLoginMethod] = useState<LoginMethod>('phone');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPasswordInput, setShowPasswordInput] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [isLoggingIn, startLoggingIn] = useTransition();
  const [socialLoading, setSocialLoading] = useState<'google' | 'apple' | null>(null);
  const analytics = useRudderStackAnalytics();
  const {
    data: session,
    update
  } = useSession();
  const [isWebView] = useIsWebview();
  const firstTouchUTM = useFirstTouchUTM();
  const ifpCampaignParams = useIfpCampaignParams();
  const hasTrackedPageView = useRef(false);
  const [emailSuggestion, setEmailSuggestion] = useState<EmailDomainSuggestion | null>(null);
  const hasShownSuggestion = useRef(false);
  const handleEmailBlur = useCallback(() => {
    if (!email) {
      setEmailSuggestion(null);
      return;
    }
    const suggestion = checkEmailDomain(email);
    setEmailSuggestion(suggestion.hasTypo ? suggestion : null);
  }, [email]);
  const acceptEmailSuggestion = useCallback(() => {
    if (emailSuggestion?.suggested) {
      setEmail(emailSuggestion.suggested);
      setEmailSuggestion(null);
    }
  }, [emailSuggestion]);
  usePageTransition();
  useEffect(() => {
    const prefill = resolvePrefillEmail(searchParams.get('email'));
    if (prefill) {
      setEmail(prefill);
      setLoginMethod('email');
    }
  }, [searchParams]);

  // Handle WebView redirect for deleted accounts and session expiry - notify native app
  useEffect(() => {
    const error = searchParams.get('error');
    const callbackUrl_0 = searchParams.get('callbackUrl');
    if (isWebView && typeof window !== 'undefined' && (window as any).ReactNativeWebView) {
      // Account deleted, user not found, or exchange token errors - force logout
      if (error === 'account_deleted' || error === 'user_not_found' || error === 'no_token' || error === 'invalid_token' || error === 'exchange_failed') {
        (window as any).ReactNativeWebView.postMessage(JSON.stringify({
          type: 'LOGOUT'
        }));
      }
      // Session expired - redirect means session issue, no explicit error
      else if (callbackUrl_0 && !error) {
        (window as any).ReactNativeWebView.postMessage(JSON.stringify({
          type: 'SESSION_EXPIRED',
          payload: {
            callbackUrl: callbackUrl_0
          }
        }));
      }
    }
  }, [isWebView, searchParams]);
  useEffect(() => {
    if (!analytics || hasTrackedPageView.current) return;
    hasTrackedPageView.current = true;
    analytics.page('', {
      page_name: 'Login',
      platform: isWebView ? 'app' : 'web',
      is_logged_in: !!session?.user?.id,
      ...(firstTouchUTM || {})
    });
  }, [analytics, isWebView, session?.user?.id, firstTouchUTM]);
  const handleChangePhoneNumber = (e: ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(/\D/g, '');
    if (value.length > 11) {
      value = value.slice(0, 11);
    }
    if (value.length > 2) {
      value = `(${value.slice(0, 2)}) ${value.slice(2)}`;
    }
    if (value.length > 10) {
      value = `${value.slice(0, 10)}-${value.slice(10)}`;
    }
    setPhoneNumber(value);

    // Reset password flow when user changes phone
    if (showPasswordInput) {
      setShowPasswordInput(false);
      setPassword('');
      setPasswordVisible(false);
    }
  };
  const handleChangeEmail = (e_0: ChangeEvent<HTMLInputElement>) => {
    setEmail(e_0.target.value);
    setEmailSuggestion(null);
    hasShownSuggestion.current = false;

    // Reset password flow when user changes email
    if (showPasswordInput) {
      setShowPasswordInput(false);
      setPassword('');
      setPasswordVisible(false);
    }
  };
  const handlePhoneSubmit = useCallback(async () => {
    if (!phoneNumber) {
      toast({
        type: 'error',
        description: 'Preencha o número de telefone para continuar'
      });
      return;
    }
    const formattedPhone = sanitizeBrazilianPhone(phoneNumber);
    if (!formattedPhone) {
      toast({
        type: 'error',
        description: 'Número de telefone inválido. Use o formato (DDD) 9XXXX-XXXX.'
      });
      return;
    }
    setShowPasswordInput(true);
  }, [phoneNumber]);
  const handleEmailSubmit = useCallback(async () => {
    if (!email) {
      toast({
        type: 'error',
        description: 'Preencha o email para continuar'
      });
      return;
    }

    // First submit with a typo: show suggestion and stop.
    // Second submit (user saw it and clicked again): let through.
    const suggestion_0 = checkEmailDomain(email);
    if (suggestion_0.hasTypo && !hasShownSuggestion.current) {
      setEmailSuggestion(suggestion_0);
      hasShownSuggestion.current = true;
      return;
    }
    setShowPasswordInput(true);
  }, [email]);
  const handleLoginResult = useCallback(async (result: {
    status: 'idle' | 'in_progress' | 'success' | 'failed' | 'invalid_data' | 'rate_limited';
    retryAfter?: number;
    user?: {
      id: string;
      name: string;
      email?: string;
      phone?: string;
    };
    subscription?: {
      hasActiveSubscription: boolean;
      subscriptionType?: string;
      billingPeriod?: string;
    };
    homePage?: string;
  }, method: 'email' | 'phone', identify: {
    email?: string;
    phone?: string;
  }) => {
    const trackLoginFailed = (errorMessage: string) => {
      analytics?.track('Login Failed', {
        login_method: method,
        platform: isWebView ? 'app' : 'web',
        timestamp: new Date().toLocaleString('pt-BR'),
        error: loginErrorCodeFromStatus(result.status),
        error_message: errorMessage
      });
    };
    if (result.status === 'rate_limited') {
      toast({
        type: 'error',
        description: 'Muitas tentativas. Tente novamente mais tarde.'
      });
      return;
    }
    if (result.status === 'failed') {
      toast({
        type: 'error',
        description: 'Dados de acesso incorretos.'
      });
      trackLoginFailed('Dados de acesso incorretos.');
      return;
    }
    if (result.status === 'invalid_data') {
      toast({
        type: 'error',
        description: 'Dados inválidos. Tente novamente.'
      });
      trackLoginFailed('Dados inválidos. Tente novamente.');
      return;
    }
    if (result.status === 'success') {
      const firstName = result.user?.name?.split(' ')[0];
      toast({
        type: 'success',
        description: firstName ? `Bem vindo de volta, ${firstName}!` : 'Login realizado com sucesso!'
      });
      if (result.user?.id) {
        await analytics?.identify(result.user.id, {
          name: result.user.name,
          email: identify.email,
          phone: identify.phone,
          createdAt: new Date().toLocaleString('pt-BR'),
          timestamp: new Date().toISOString(),
          plan: result.subscription?.subscriptionType ?? 'basic',
          has_active_subscription: result.subscription?.hasActiveSubscription ?? false,
          last_login: new Date().toISOString(),
          ...mergeTrackingParams(firstTouchUTM, ifpCampaignParams)
        });
      }
      analytics?.track('Login Successful', {
        login_method: method,
        platform: isWebView ? 'app' : 'web',
        timestamp: new Date().toLocaleString('pt-BR')
      });
      const ALLOWED_HOME_PAGES = new Set(['/chat', '/dashboard']);
      const dbHomePage = result.homePage ? `/${result.homePage}` : undefined;
      const homePage = dbHomePage && ALLOWED_HOME_PAGES.has(dbHomePage) ? dbHomePage : getHomePage(result.user?.id);
      const redirectUrl = getSafeRedirectUrl(callbackUrl, homePage);
      // Re-seed the root SessionProvider's client state immediately. signIn()
      // set the session cookie but did not touch the provider's React state,
      // and router.push() is a soft nav — the root layout never re-runs on
      // client navigation, so auth() isn't re-called. Without this, every
      // useSession() consumer on the target route sees a stale null session
      // for up to 5 min (refetchInterval), which is what bounced the
      // /my-account subscribe flow to /register (PIERRE-1603). This is the
      // root-cause fix and replaces the per-route SessionProvider workaround.
      //
      // try/finally: if the refetch rejects (e.g. transient network), still
      // navigate — the session cookie is already set by signIn(), so the
      // provider refreshes via refetchInterval/refetchOnWindowFocus. Avoids
      // leaving the user stuck on the login page.
      try {
        await update();
      } finally {
        router.push(redirectUrl);
      }
    }
  }, [analytics, isWebView, router, callbackUrl, firstTouchUTM, ifpCampaignParams, update]);
  const handleLoginSubmit = useCallback(async () => {
    if (!password) {
      toast({
        type: 'error',
        description: 'Digite sua senha para continuar'
      });
      return;
    }
    const formData = new FormData();
    if (loginMethod === 'phone') {
      const formattedPhone_0 = sanitizeBrazilianPhone(phoneNumber);
      if (!formattedPhone_0) {
        toast({
          type: 'error',
          description: 'Número de telefone inválido.'
        });
        return;
      }
      formData.append('phoneNumber', formattedPhone_0);
      formData.append('password', password);
      startLoggingIn(async () => {
        const result_0 = await loginWithPhone({
          status: 'idle'
        }, formData);
        await handleLoginResult(result_0, 'phone', {
          email: result_0.user?.email,
          phone: formattedPhone_0
        });
      });
    } else {
      formData.append('email', email);
      formData.append('password', password);
      startLoggingIn(async () => {
        const result_1 = await login({
          status: 'idle'
        }, formData);
        await handleLoginResult(result_1, 'email', {
          email,
          phone: result_1.user?.phone
        });
      });
    }
  }, [password, loginMethod, phoneNumber, email, handleLoginResult]);
  const buttonContent = useMemo(() => {
    if (isLoggingIn) {
      return <div className="flex items-center gap-2">
          <Spinner size="lg" spinnerClassName="bg-black md:bg-white md:dark:bg-black" />
          <span>Entrando...</span>
        </div>;
    }
    return showPasswordInput ? 'Entrar' : 'Começar';
  }, [isLoggingIn, showPasswordInput]);
  return <div className="min-h-screen w-full flex bg-white dark:bg-black">
      <div className="w-full lg:flex-1 flex flex-col lg:p-16">
        <div className="flex-1 flex flex-col justify-center px-4 py-10 gap-12 md:gap-10 max-w-md mx-auto w-full">
          <div className="flex flex-col gap-10 md:gap-8">
            <div className="flex flex-col gap-3 text-center md:text-left">
              <div className="flex justify-center md:justify-start mb-2">
                <div className="border-[0.5px] border-white/20 dark:border-white/20 rounded-xl bg-transparent">
                  <PierreRiveIdle className="size-[56px]" />
                </div>
              </div>

              <div className="flex flex-col md:items-start items-center gap-2">
                <h2 className="text-primary text-3xl lg:text-4xl font-bold">
                  {showPasswordInput ? 'Bem vindo de volta' : loginMethod === 'phone' ? 'Qual seu Whatsapp?' : 'Qual seu email?'}
                </h2>

                <span className="text-muted-foreground text-sm md:text-base">
                  {showPasswordInput ? 'Insira sua senha para continuar' : 'Inicie seu cadastro ou acesse sua conta.'}
                </span>
              </div>
            </div>

            <div className="space-y-4">
              {/* Phone Number Input */}
              {loginMethod === 'phone' && <div className="relative flex gap-4">
                  <div className="h-14 select-none opacity-50 cursor-not-allowed px-4 gap-2 py-3 text-lg rounded-xl border-input bg-transparent flex justify-center items-center w-fit border ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-colors">
                    <Image src="/images/brasil-flag.svg" alt="bandeira do Brasil" width={25} height={25} />
                    <span className="text-muted-foreground">+55</span>
                  </div>

                  <Input name="phoneNumber" autoComplete="phoneNumber" value={phoneNumber} onChange={handleChangePhoneNumber} inputSize="xl" rounded="2xl" variant="outline" inputMode="numeric" maxLength={15} floatingLabel label="Número do WhatsApp" />
                </div>}

              {/* Email Input */}
              {loginMethod === 'email' && <div>
                  <div className="relative">
                    <Input type="email" name="email" autoComplete="off" value={email} onChange={handleChangeEmail} onBlur={handleEmailBlur} inputSize="xl" rounded="2xl" variant="outline" floatingLabel label="Digite seu email" className="pr-12" />
                    <EmailDomainAutocomplete email={email} onSelect={completed => {
                  setEmail(completed);
                  setEmailSuggestion(null);
                }} visible={!showPasswordInput} />
                  </div>
                  {emailSuggestion && !showPasswordInput && <button type="button" onClick={acceptEmailSuggestion} className="mt-1 pl-4 text-sm text-muted-foreground hover:text-primary transition-colors text-left w-full">
                      Você quis dizer:{' '}
                      <span className="font-semibold text-primary underline">
                        {emailSuggestion.suggested}
                      </span>
                      ?
                    </button>}
                </div>}

              {/* Password input follows a valid identifier without checking account existence. */}
              <AnimatePresence mode="wait">
                {showPasswordInput && <motion.div initial={{
                opacity: 0,
                y: -10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} transition={{
                duration: 0.3,
                ease: 'easeOut'
              }} className="space-y-2">
                    <div className="relative">
                      <Input type={passwordVisible ? 'text' : 'password'} name="password" autoComplete="password" value={password} onChange={e_1 => setPassword(e_1.target.value)} inputSize="xl" rounded="2xl" variant="outline" className="pr-12" label="Digite sua senha" floatingLabel onKeyDown={e_2 => {
                    if (e_2.key === 'Enter') {
                      handleLoginSubmit();
                    }
                  }} />
                      <button type="button" onClick={() => setPasswordVisible(!passwordVisible)} className="absolute top-1/2 right-4 -translate-y-1/2 text-primary/50 hover:text-primary transition-colors">
                        {passwordVisible ? <EyeOff className="size-5" /> : <Eye className="size-5" />}
                      </button>
                    </div>

                    <div className="flex">
                      <Link href="/reset-password/request" prefetch={false} onClick={() => {
                    if (loginMethod === 'email' && email) {
                      setPrefillEmail(email);
                    }
                  }} className="text-sm text-muted-foreground hover:text-[#a3ff12] transition-colors">
                        Esqueceu a senha?
                      </Link>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>
          </div>

          <div className="w-full space-y-3">
            <Button type="button" rounded="full" className="w-full" onClick={showPasswordInput ? handleLoginSubmit : loginMethod === 'phone' ? handlePhoneSubmit : handleEmailSubmit} size="xl" disabled={isLoggingIn}>
              {buttonContent}
            </Button>

            <div className="relative flex items-center justify-center">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-muted-foreground/20" />
              </div>
              <div className="relative bg-white dark:bg-black px-4">
                <span className="text-xs text-muted-foreground uppercase">
                  ou
                </span>
              </div>
            </div>

            <Button type="button" variant="outline" rounded="full" className="w-full" size="xl" onClick={() => {
            setLoginMethod(loginMethod === 'phone' ? 'email' : 'phone');
            setPhoneNumber('');
            setEmail('');
            setPassword('');
            setShowPasswordInput(false);
            setPasswordVisible(false);
          }}>
              {loginMethod === 'phone' ? <Mail className="size-5" /> : <MessageCircle className="size-5" />}
              {loginMethod === 'phone' ? 'Logar com E-mail' : 'Logar com WhatsApp'}
            </Button>

            <p className="text-center text-sm text-muted-foreground">
              Ainda não tem uma conta?{' '}
              <Link href="/register" className="font-medium text-primary underline transition-colors hover:text-[#a3ff12]">
                Criar conta
              </Link>
            </p>

            {ENABLE_SOCIAL_AUTH && <>
                <Button type="button" variant="outline" rounded="full" className="w-full" size="xl" disabled={socialLoading !== null} onClick={() => {
              setSocialLoading('google');
              nextAuthSignIn('google', {
                callbackUrl: callbackUrl || '/login'
              });
            }}>
                  {socialLoading === 'google' ? <Spinner size="lg" spinnerClassName="bg-black dark:bg-white" /> : <svg className="size-5" viewBox="0 0 24 24">
                      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
                      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                    </svg>}
                  Continuar com Google
                </Button>

                <Button type="button" variant="outline" rounded="full" className="w-full" size="xl" disabled={socialLoading !== null} onClick={() => {
              setSocialLoading('apple');
              nextAuthSignIn('apple', {
                callbackUrl: callbackUrl || '/login'
              });
            }}>
                  {socialLoading === 'apple' ? <Spinner size="lg" spinnerClassName="bg-white dark:bg-black" /> : <svg className="size-5 fill-current" viewBox="0 0 24 24">
                      <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
                    </svg>}
                  Continuar com Apple
                </Button>
              </>}

            <div className="text-center">
              <p className="text-muted-foreground text-sm">
                Ao continuar você concorda com os{' '}
                <Link href="/privacy-policy" className="underline hover:text-[#a3ff12] transition-colors">
                  Termos de uso
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="hidden lg:block w-[800px] max-w-[850px] m-3 lg:w-[55%] relative">
        <BackgroundVideo src="/videos/background.webm" className="rounded-3xl" />
      </div>
    </div>;
}